// ---------------------------------------------------------------------------
// Sending another copy of a document that has already been printed.
//
// A reprint is a DIFFERENT KIND OF JOB from the one the sale produced, and the
// distinction is the whole reason this hook exists rather than reusing the
// sale's print path:
//
//   an ORIGINAL job is deduplicated. One receipt per sale, one ticket per
//   order, however many times the till asks;
//
//   a REPRINT is deliberate. Somebody pressed a button because the first copy
//   was lost, smudged or handed to the wrong customer, and refusing it as a
//   duplicate would be refusing the thing they asked for.
//
// So the server gets `isReprint`, which puts a `unique` value into the
// idempotency key. A reprint can never collide with the original, and two
// reprints of the same sale are two separate jobs - which is correct, because
// two people pressed the button twice.
//
// What it renders is the SAME sheet the screen is showing, at the SAME design
// version the record was written under. A reprint that came out looking like
// this month's design would not be a copy of anything.
// ---------------------------------------------------------------------------

import { useCallback, useRef, useState } from 'react';
import { fetchPrintJobStatus, useQueuePrintJob, type PrintJobStatus } from '../api/printJobs.api';
import { dotsForRoll, rasteriseElement, RasterError } from '../utils/rasterise';

/// NO_AGENT: the document is saved, but nothing in the shop is listening -
/// the print agent on the till PC has not been heard from.
export type ReprintStatus = 'IDLE' | 'PREPARING' | PrintJobStatus | 'NO_AGENT';

/// How long a copy is followed before the till stops waiting. The shop's
/// agent takes work every five seconds; a little over two ticks.
const FOLLOW_MS = 12_000;
const FOLLOW_EVERY_MS = 1_000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface ReprintTarget {
  printerRole: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  /// WHICH PRINTER. The shop's chosen receipt printer for a receipt, the
  /// bench a ticket was written for when reprinting one.
  ///
  /// This used to be absent, and the queue fell back to "whichever printer has
  /// held this role longest". On a shop that owns one receipt printer that was
  /// the same device; on a shop that has since added another, or moved its
  /// receipts to a different one, a reprint came out of the wrong machine -
  /// usually the one at the back.
  printerProfileId?: string | null;
  /// Defaults to the document the printer role implies. A HELD_ORDER_SHEET
  /// says so explicitly: it is not a receipt, and the server gives it its own
  /// origin so it can never be counted as one.
  documentType?: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET' | 'HELD_ORDER_SHEET';
  /// The cart a held-order sheet was printed from.
  heldOrderId?: string | null;
  /// The sheet on screen. Read at press time, not at render time, so a sheet
  /// that mounts late is still found.
  element: () => HTMLElement | null;
  widthMm: 58 | 80;
  saleId?: string | null;
  orderId?: string | null;
  kitchenTicketId?: string | null;
  /// The version the record was written under, so the copy matches the
  /// original rather than today's design.
  designVersionId?: string | null;
}

export function useNetworkReprint() {
  const queuePrint = useQueuePrintJob();
  const [status, setStatus] = useState<ReprintStatus>('IDLE');
  const [error, setError] = useState<string | null>(null);
  /// The same reason, readable the moment `send` returns. The state above
  /// only reaches the NEXT render, so a caller reading `error` straight after
  /// `await send(...)` always saw null and never showed why a copy failed.
  const lastErrorRef = useRef<string | null>(null);

  const send = useCallback(
    async (target: ReprintTarget) => {
      setStatus('PREPARING');
      setError(null);
      lastErrorRef.current = null;

      try {
        const element = target.element();
        if (!element) throw new RasterError('That document could not be prepared.');

        const raster = await rasteriseElement(element, {
          pixelWidth: dotsForRoll(target.widthMm),
        });

        const isCashier = target.printerRole === 'CASHIER_RECEIPT';
        const documentType =
          target.documentType ?? (isCashier ? 'CASHIER_RECEIPT' : 'KITCHEN_TICKET');
        const isHeld = documentType === 'HELD_ORDER_SHEET';

        const response = await queuePrint.mutateAsync({
          printerRole: target.printerRole,
          printerProfileId: target.printerProfileId ?? null,
          documentType,
          raster,
          saleId: target.saleId ?? null,
          orderId: target.orderId ?? null,
          kitchenTicketId: target.kitchenTicketId ?? null,
          heldOrderId: target.heldOrderId ?? null,
          ...(isCashier
            ? { receiptDesignVersionId: target.designVersionId ?? null }
            : { kitchenDesignVersionId: target.designVersionId ?? null }),
          // What makes this a new piece of paper rather than a rejected
          // duplicate. `unique` is what keeps the key off the original's.
          //
          // A held-order sheet is NOT flagged as a reprint: it is not a copy
          // of a receipt, it is its own kind of document, and the server
          // gives it an origin of its own from the document type.
          ...(isHeld ? {} : { isReprint: true }),
          unique: `${isHeld ? 'held' : 'reprint'}-${Date.now()}`,
          immediate: true,
        });

        let settled: ReprintStatus = response.result?.status ?? response.job.status;
        let problem: string | null = response.result?.error ?? null;

        // IN THE CLOUD every job starts QUEUED: the print agent on the till PC
        // takes it within seconds. "Queued" therefore said nothing - the till
        // used to announce "waiting to retry" for a copy that printed a moment
        // later. Follow the job to what really happened.
        if (settled === 'QUEUED' || settled === 'PRINTING') {
          if (response.awaitingAgent && !response.awaitingAgent.seenRecently) {
            settled = 'NO_AGENT';
          } else {
            const deadline = Date.now() + FOLLOW_MS;
            while (Date.now() < deadline) {
              await sleep(FOLLOW_EVERY_MS);
              try {
                const now = await fetchPrintJobStatus(response.job.id);
                if (now.status !== 'QUEUED' && now.status !== 'PRINTING') {
                  settled = now.status;
                  problem = now.error;
                  break;
                }
              } catch {
                // A missed look is not an outcome; look again.
              }
            }
          }
        }

        setStatus(settled);
        setError(problem);
        lastErrorRef.current = problem;
        return settled;
      } catch (caught) {
        const reason =
          caught instanceof RasterError
            ? caught.message
            : caught instanceof Error && caught.message
              ? caught.message
              : null;
        setStatus('FAILED');
        setError(reason);
        lastErrorRef.current = reason;
        return 'FAILED' as const;
      }
    },
    [queuePrint],
  );

  const reset = useCallback(() => {
    setStatus('IDLE');
    setError(null);
  }, []);

  const lastError = useCallback(() => lastErrorRef.current, []);

  return { status, error, lastError, send, reset, isSending: status === 'PREPARING' };
}
